import { useCallback, useState } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Menu } from "antd";
import { clsx } from "clsx";
import { $getSelection, $isRangeSelection, TextNode } from "lexical";
import * as ReactDOM from "react-dom";

import { $createCustomVariableNode, $isCustomVariableNode } from "../../nodes/CustomVariableNode";
import { LexicalTriggerMenuPlugin } from "../../vendor/LexicalTriggerMenuPlugin";
import {
    ConfigurableVariableSubMenuItem,
    MenuItem,
} from "./Components/ConfigurableVariableSubMenuItem/ConfigurableVariableSubMenuItem";
import { ConfigurableVariableOption } from "./ConfigurableVariableOption";
import { useTypeaheadTriggerMatch } from "./Hooks/useTypeaheadTriggerMatch/useTypeaheadTriggerMatch";

import type { LexicalTrigger } from "../../types";

import styles from "./ConfigurableVariablesMenuPickerPlugin.module.scss";

const CustomVariableMenuPickerPluginSizes = {
    default: undefined,
    large: 200,
} as const;

type CustomVariableMenuPickerPluginProps = {
    popAbove?: boolean;
    fixedWidth?: boolean;
    prioritizedTriggers: Array<LexicalTrigger>;
    loading: boolean;
    options: Array<ConfigurableVariableOption>;
    menuItems: MenuItem[];
    menuSize?: keyof typeof CustomVariableMenuPickerPluginSizes;
};

function detectKeyPrefix(array: Array<ConfigurableVariableOption>): string | undefined {
    if (array.length === 0) {
        return "other";
    }

    // Extract the prefix of the first item
    const firstPrefix = array[0].key.split(".")[0]?.split("_")?.[0];

    // Check if all items share the same prefix
    const allSamePrefix = array.every((item) => item.key.startsWith(firstPrefix));

    if (allSamePrefix) {
        return firstPrefix;
    } else {
        return undefined;
    }
}

export function ConfigurableVariablesMenuPickerPlugin({
    popAbove = false,
    fixedWidth = false,
    loading,
    options,
    prioritizedTriggers,
    menuItems = [],
    menuSize = "default",
}: CustomVariableMenuPickerPluginProps) {
    const [editor] = useLexicalComposerContext();
    const [typeaheadOptions, setTypeaheadOptions] = useState(options);

    const { triggerCallbacks } = useTypeaheadTriggerMatch({
        prioritizedTriggers,
    });

    const handleQueryChange = useCallback(
        (query: string | null) => {
            let filteredOptions = options;
            const safeQuery = query?.trim()?.toLowerCase();
            if (safeQuery) {
                filteredOptions = filteredOptions.filter((option) => option.value.toLowerCase().includes(safeQuery));
            }
            setTypeaheadOptions(filteredOptions);
        },
        [options]
    );

    const onSelectOption = useCallback(
        (
            selectedOption: ConfigurableVariableOption,
            nodeToRemove: TextNode | null,
            closeMenu: () => void,
            matchedString: string,
            openTag: string = "{{",
            closeTag: string = "}}"
        ) => {
            editor.update(() => {
                const selection = $getSelection();

                if (!$isRangeSelection(selection) || selectedOption == null) {
                    return;
                }

                if (nodeToRemove) {
                    nodeToRemove.remove();
                } else {
                    const [currentNode] = selection.getNodes();
                    if ($isCustomVariableNode(currentNode) && matchedString.length) {
                        currentNode.remove();
                    }
                }

                selection.insertNodes([$createCustomVariableNode(`${openTag}${selectedOption.text}${closeTag}`)]);
            });
            closeMenu();
        },
        [editor]
    );

    const menuSizeValue = CustomVariableMenuPickerPluginSizes[menuSize];
    const menuStyle = menuSizeValue ? { width: `${menuSizeValue}px` } : undefined;

    return (
        <LexicalTriggerMenuPlugin
            onQueryChange={handleQueryChange}
            onSelectOption={onSelectOption}
            triggerFns={triggerCallbacks}
            options={typeaheadOptions}
            fixedMenu={popAbove}
            menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
                if (anchorElementRef.current == null) {
                    return null;
                }

                if (loading) {
                    return ReactDOM.createPortal(
                        <div
                            className={clsx(styles.nestedMenu, "typeahead-popover custom-variables-menu", {
                                "typeahead-popover-popabove": popAbove,
                            })}
                        >
                            <div className={styles.loading}>Loading...</div>
                        </div>,
                        anchorElementRef.current
                    );
                }

                let menuExtraProps = {};
                const hasSelectedPrefix = detectKeyPrefix(typeaheadOptions);

                if (hasSelectedPrefix) {
                    menuExtraProps = {
                        openKeys: [hasSelectedPrefix],
                    };
                }

                return anchorElementRef.current && typeaheadOptions.length
                    ? ReactDOM.createPortal(
                          <div
                              className={clsx(styles.menuContainer, "typeahead-popover custom-variables-menu", {
                                  "typeahead-popover-popabove": popAbove,
                                  "typeahead-popover-fixedsize": fixedWidth,
                              })}
                          >
                              <Menu mode="vertical" className={styles.menu} style={menuStyle} {...menuExtraProps}>
                                  {menuItems.map((item) => (
                                      <ConfigurableVariableSubMenuItem
                                          key={item.key}
                                          item={item}
                                          options={typeaheadOptions}
                                          selectedIndex={selectedIndex}
                                          hasSelectedPrefix={hasSelectedPrefix}
                                          onClick={(option: ConfigurableVariableOption, index: number) => {
                                              setHighlightedIndex(index);
                                              selectOptionAndCleanUp(option);
                                          }}
                                      />
                                  ))}
                              </Menu>
                              <div className={styles.arrow}></div>
                          </div>,
                          anchorElementRef.current
                      )
                    : null;
            }}
        />
    );
}
